import { Asset, AssetState, FolderContent } from '@/api/generatedApi';
import { isAfter, parseISO } from 'date-fns';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { GET_ASSET_THUMBNAIL_QUERY_KEY } from '../api/assets/useGetAssetThumbnail';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '../api/folders/useFetchFolderContents';
import { RefetchManager } from './refetchManager';

export const useAssetPolling = (parameters: { row: FolderContent }) => {
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const refetchManager = RefetchManager.getInstance();
  const queryClient = useQueryClient();

  const { asset: fetchedAsset, refetch } = useGetAsset({
    assetId: parameters.row.id,
    enabled: shouldRefetch,
  });

  const asset =
    !fetchedAsset || isAfter(parseISO(parameters.row.updatedAt), parseISO(fetchedAsset.updatedAt))
      ? (parameters.row as Asset)
      : fetchedAsset;

  useEffect(() => {
    if (parameters.row.contentType === 'asset' && parameters.row.state === AssetState.Indexing) {
      refetchManager.requestRefetch(parameters.row.id, () => {
        setShouldRefetch(true);
        refetch();
      });
    }
  }, [parameters.row.id, parameters.row.contentType, parameters.row, refetch]);

  useEffect(() => {
    if (fetchedAsset && fetchedAsset.state !== AssetState.Indexing) {
      setShouldRefetch(false);
      refetchManager.completeRefetch(parameters.row.id);

      if (fetchedAsset.state === AssetState.Finished) {
        queryClient.resetQueries({
          queryKey: [GET_ASSET_THUMBNAIL_QUERY_KEY, { assetId: parameters.row.id }],
        });

        if (fetchedAsset.isRootAssembly) {
          queryClient.resetQueries({
            queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
            exact: false,
          });
        }
      }
    }
  }, [fetchedAsset]);

  useEffect(() => {
    return () => {
      refetchManager.completeRefetch(parameters.row.id);
    };
  }, [parameters.row.id]);

  return { data: asset, fetchedData: fetchedAsset };
};
